import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, UserPlus } from 'lucide-react';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  addDoc,
  serverTimestamp,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { Button, Table, Input, Dropdown } from '../components/ui';
import AddNewPatientModal from '../components/AddNewPatientModal';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { db } from '../lib/firebase';
import { Patient, SessionPriceTier } from '../types/firestoreTypes';
import { PATIENT_STATUS } from '../types/constants';

const PATIENTS_PER_PAGE = 20;
const SEARCH_LIMIT = 200; // Load more patients when searching

const StatusPill: React.FC<{ status: Patient['status']; label: string }> = ({
  status,
  label,
}) => {
  const statusStyles = {
    Prospect: 'bg-blue-50 text-blue-700',
    Onboarding: 'bg-amber-50 text-amber-700',
    Active: 'bg-[#ECFDF3] text-[#027A48]',
    Closed: 'bg-gray-100 text-gray-600',
  };

  return (
    <span
      className={`inline-flex px-2 py-1 rounded-full text-sm font-medium ${
        statusStyles[status] || 'bg-gray-100 text-gray-600'
      }`}
    >
      {label}
    </span>
  );
};

const PatientsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();

  const [patients, setPatients] = useState<Patient[]>([]);
  const [priceTiers, setPriceTiers] = useState<SessionPriceTier[]>([]);
  const [loading, setLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(PATIENT_STATUS.ACTIVE);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [lastVisible, setLastVisible] =
    useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const statusOptions = [
    { value: '', label: t('patients.filters.allStatuses') },
    { value: PATIENT_STATUS.PROSPECT, label: t('patients.status.Prospect') },
    {
      value: PATIENT_STATUS.ONBOARDING,
      label: t('patients.status.Onboarding'),
    },
    { value: PATIENT_STATUS.ACTIVE, label: t('patients.status.Active') },
    { value: PATIENT_STATUS.CLOSED, label: t('patients.status.Closed') },
  ];

  const fetchPriceTiers = useCallback(async () => {
    if (!user) return;
    try {
      const q = query(
        collection(db, 'sessionPriceTiers'),
        where('therapistId', '==', user.uid),
        where('isArchived', '==', false),
        orderBy('tierName')
      );
      const querySnapshot = await getDocs(q);
      const tiers = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...(doc.data() as Omit<SessionPriceTier, 'id'>),
      }));
      setPriceTiers(tiers);
    } catch (err) {
      console.error('Error fetching price tiers:', err);
      setErrorKey('patients.errors.loadPriceTiersFailed');
      addToast({
        type: 'error',
        message: t('patients.errors.loadPriceTiersFailed'),
      });
    }
  }, [user, t, addToast]);

  const fetchPatients = useCallback(
    async (cursor?: QueryDocumentSnapshot<DocumentData>) => {
      if (!user) return;

      try {
        if (cursor) {
          setIsFetchingMore(true);
        } else {
          setLoading(true);
        }
        setErrorKey(null);

        let baseQuery = query(
          collection(db, 'patients'),
          where('therapistId', '==', user.uid)
        );

        if (statusFilter) {
          baseQuery = query(baseQuery, where('status', '==', statusFilter));
        }

        // Skip Firestore text search - handle all search client-side
        // This ensures consistent multi-field search regardless of status filter

        // Use higher limit when searching to find patients across more pages
        const queryLimit = searchTerm ? SEARCH_LIMIT : PATIENTS_PER_PAGE;
        let finalQuery = query(
          baseQuery,
          orderBy('name'),
          limit(queryLimit)
        );

        if (cursor) {
          finalQuery = query(finalQuery, startAfter(cursor));
        }

        const querySnapshot = await getDocs(finalQuery);
        let fetchedPatients = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...(doc.data() as Omit<Patient, 'id'>),
        }));

        // Client-side filtering for multi-field search
        // Note: When searching, we fetch more records (SEARCH_LIMIT) to increase
        // the likelihood of finding patients across multiple pages
        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase();
          fetchedPatients = fetchedPatients.filter((patient) => {
            return (
              patient.name?.toLowerCase().includes(searchLower) ||
              patient.email?.toLowerCase().includes(searchLower) ||
              patient.phone?.toLowerCase().includes(searchLower)
            );
          });
        }

        setPatients((prev) =>
          cursor ? [...prev, ...fetchedPatients] : fetchedPatients
        );
        setLastVisible(
          querySnapshot.docs[querySnapshot.docs.length - 1] || null
        );
        // Determine if there are more pages based on search/browse mode
        const expectedLimit = searchTerm ? SEARCH_LIMIT : PATIENTS_PER_PAGE;
        setHasMore(querySnapshot.docs.length === expectedLimit);
      } catch (err) {
        console.error('Error fetching patients:', err);
        setErrorKey('patients.errors.loadPatientsFailed');
        addToast({
          type: 'error',
          message: t('patients.errors.loadPatientsFailed'),
        });
      } finally {
        setLoading(false);
        setIsFetchingMore(false);
      }
    },
    [user, searchTerm, statusFilter, addToast, t]
  );

  useEffect(() => {
    if (user) {
      fetchPriceTiers();
    }
  }, [fetchPriceTiers, user]);

  useEffect(() => {
    if (user) {
      setPatients([]);
      setLastVisible(null);
      setHasMore(true);
      fetchPatients();
    }
  }, [user, searchTerm, statusFilter, fetchPatients]);

  const loadMore = async () => {
    if (!isFetchingMore && hasMore && lastVisible) {
      await fetchPatients(lastVisible);
    }
  };

  const handleSavePatient = async (
    formData: Omit<Patient, 'id' | 'therapistId' | 'createdAt'>
  ) => {
    if (!user) return;

    setErrorKey(null);
    try {
      await addDoc(collection(db, 'patients'), {
        ...formData,
        therapistId: user.uid,
        createdAt: serverTimestamp(),
      });

      addToast({
        type: 'success',
        message: t('patients.success.patientAdded'),
        duration: 5000,
      });

      setIsModalOpen(false);
      setPatients([]);
      setLastVisible(null);
      setHasMore(true);
      fetchPatients();
    } catch (err) {
      console.error('Error saving patient:', err);
      setErrorKey('patients.errors.savePatientFailed');
      addToast({
        type: 'error',
        message: t('patients.errors.savePatientFailed'),
      });
    } finally {
      // Cleanup handled by finally block
    }
  };

  const columns = [
    { key: 'name', header: t('patients.table.headers.name') },
    { key: 'email', header: t('patients.table.headers.email') },
    { key: 'phone', header: t('patients.table.headers.phone') },
    {
      key: 'status',
      header: t('patients.table.headers.status'),
      render: (patient: Patient) => (
        <StatusPill
          status={patient.status}
          label={t(`patients.status.${patient.status}`)}
        />
      ),
    },
    {
      key: 'createdAt',
      header: t('patients.table.headers.created'),
      render: (patient: Patient) =>
        patient.createdAt?.toDate().toLocaleDateString() ||
        t('patients.table.invalidDate'),
    },
  ];

  if (errorKey && !loading && patients.length === 0) {
    return (
      <div className="p-4 bg-red-50 rounded-lg text-red-700">{t(errorKey)}</div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <h1 className="text-2xl font-semibold text-[#101828]">
          {t('patients.heading')}
        </h1>
        <Button
          variant="primary"
          onClick={() => setIsModalOpen(true)}
          leftIcon={<UserPlus className="w-4 h-4" />}
        >
          {t('patients.buttons.addNew')}
        </Button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-[#EAECF0] p-6">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <Input
            placeholder={t('patients.filters.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leftIcon={<Search className="w-5 h-5" />}
            className="w-full sm:w-80"
            aria-label={t('patients.filters.searchPlaceholder')}
          />

          <Dropdown
            options={statusOptions}
            value={statusFilter}
            onChange={(value) => setStatusFilter(value)}
            className="w-full sm:w-48"
            aria-label={t('patients.filters.statusPlaceholder')}
          />
        </div>

        {patients.length === 0 && !loading ? (
          <div className="py-8 text-center text-[#667085]">
            {t('patients.table.noData')}
          </div>
        ) : (
          <Table
            columns={columns}
            data={patients}
            isLoading={loading && !isFetchingMore}
            onRowClick={(patient) => navigate(`/patients/${patient.id}`)}
            pagination={undefined}
          />
        )}

        {hasMore && !loading && (
          <div className="mt-6 text-center">
            <Button
              variant="secondary"
              onClick={loadMore}
              isLoading={isFetchingMore}
              disabled={isFetchingMore}
            >
              {t('patients.buttons.loadMore')}
            </Button>
          </div>
        )}
        {!hasMore && !loading && patients.length > 0 && (
          <p className="mt-6 text-center text-gray-500">
            {t('patients.table.noMoreData')}
          </p>
        )}
      </div>

      <AddNewPatientModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSavePatient}
        priceTiers={priceTiers}
      />
    </div>
  );
};

export default PatientsPage;
